'use memo';

import { useEffect } from 'react';

import type { SessionSummary } from '@/features/global-ai-chat/api/types';
import { useGlobalAiChatStore } from '@/features/global-ai-chat/store/globalAiChatStore';

/**
 * `activeSessionId` を sessions リストと同期する。
 *
 * - sessions が空: `activeSessionId` を null にリセット
 * - `activeSessionId` が現存セッションに無い場合（Dexie 再 seed 後の stale ID 等）: 先頭セッションへ寄せる
 *
 * 読み込み中（`isPending` true）は副作用しないので、初回ロード時の null フラッシュを起こさない。
 */
export function useReconcileActiveSession(
  sessions: SessionSummary[],
  isPending: boolean,
): void {
  const activeSessionId = useGlobalAiChatStore(
    (state) => state.activeSessionId,
  );
  const setActiveSessionId = useGlobalAiChatStore(
    (state) => state.setActiveSessionId,
  );

  useEffect(() => {
    if (isPending) return;

    if (sessions.length === 0) {
      if (activeSessionId !== null) {
        setActiveSessionId(null);
      }
      return;
    }

    const exists = sessions.some(
      (session) => session.chatSessionId === activeSessionId,
    );
    if (!activeSessionId || !exists) {
      setActiveSessionId(sessions[0]?.chatSessionId ?? null);
    }
  }, [activeSessionId, sessions, isPending, setActiveSessionId]);
}
